import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styles from '../../styles/AcceptInvite.module.scss';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://mannaplag.mannaanalytics.com/api';


interface InviteInfo {
  email: string;
  account_name: string;
  role: string;
  needs_signup: boolean;
  is_expired: boolean;
  is_invalid: boolean;
}

// ── Eye / Eye-off icons (SVG, no external deps) ──────────
function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.6 18.6 0 0 1 5.06-5.94M9.9 4.24A10.4 10.4 0 0 1 12 4c7 0 11 8 11 8a18.6 18.6 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    );
  }
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>

  );
}

// ── Shared card wrapper ──────────────────────────────────
function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.cardHeader}>
          <div className={styles.logoBadge}>
            <img
              src="/manna-plag-logo-horizontal.png?v=2"
              alt="Manna Plag"
              className={styles.logoImg}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <p className={styles.cardTitle}>Team invitation</p>
        </div>
        <div className={styles.cardBody}>{children}</div>
      </div>
    </div>
  );
}

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // Form fields for new users
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ fullName?: string; phone?: string; password?: string; confirm?: string }>({});

  // Probe the token first — send with no password to get invite info
  useEffect(() => {
    if (!token) return;
    fetch(`${BASE_URL}/accounts/accept-invite/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.error && data.error.includes('Invalid')) {
          setInfo({ email: '', account_name: '', role: '', needs_signup: false, is_expired: false, is_invalid: true });
        } else if (data.error && data.error.includes('expired')) {
          setInfo({ email: data.email || '', account_name: '', role: '', needs_signup: false, is_expired: true, is_invalid: false });
        } else if (data.needs_signup) {
          setInfo({ email: data.email, account_name: '', role: '', needs_signup: true, is_expired: false, is_invalid: false });
        } else if (data.message) {
          // Existing user — already joined
          setDone(true);
          setInfo({ email: '', account_name: data.account || '', role: data.role || '', needs_signup: false, is_expired: false, is_invalid: false });
        }
      })
      .catch(() => setError('Could not reach server. Please try again.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    const errors: { fullName?: string; phone?: string; password?: string; confirm?: string } = {};
    if (!fullName.trim()) errors.fullName = 'Full name is required.';
    if (!phone.trim()) errors.phone = 'Phone number is required.';
    else if (phone.trim().length < 10) errors.phone = 'Enter a valid phone number.';
    if (!password) errors.password = 'Password is required.';
    else if (password.length < 8) errors.password = 'Password must be at least 8 characters.';
    if (!confirm) errors.confirm = 'Please confirm your password.';
    else if (password !== confirm) errors.confirm = 'Passwords do not match.';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    setSubmitting(true); setError('');
    try {
      const res = await fetch(`${BASE_URL}/accounts/accept-invite/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, full_name: fullName, phone, password }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setDone(true);
      setInfo(prev => prev ? { ...prev, account_name: data.account, role: data.role } : prev);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────
  if (loading) return (
    <Card>
      <div className={styles.spinnerWrap}>
        <div className={styles.spinner} />
      </div>
    </Card>
  );

  // ── Invalid token ────────────────────────────────────────
  if (info?.is_invalid) return (
    <Card>
      <div className={styles.statusWrap}>
        <div className={`${styles.statusIcon} ${styles['statusIcon--error']}`}>
          <svg width="24" height="24" fill="none" stroke="#dc2626" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <p className={styles.statusTitle}>Invalid invitation</p>
        <p className={styles.statusText}>This invitation link is invalid or has already been used.</p>
        <Link to="/login" className={styles.linkBtn}>
          Go to login
        </Link>
      </div>
    </Card>
  );

  // ── Expired ──────────────────────────────────────────────
  if (info?.is_expired) return (
    <Card>
      <div className={styles.statusWrap}>
        <div className={`${styles.statusIcon} ${styles['statusIcon--warn']}`}>
          <svg width="24" height="24" fill="none" stroke="#d97706" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className={styles.statusTitle}>Invitation expired</p>
        <p className={styles.statusText}>This invitation has expired. Please ask your admin to send a new one.</p>
        <Link to="/login" className={styles.linkBtn}>
          Go to login
        </Link>
      </div>
    </Card>
  );

  // ── Success ──────────────────────────────────────────────
  if (done) return (
    <Card>
      <div className={styles.statusWrap}>
        <div className={`${styles.statusIcon} ${styles['statusIcon--success']}`}>
          <svg width="24" height="24" fill="none" stroke="#16a34a" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className={styles.statusTitle}>You're in! 🎉</p>
        <p className={styles.statusText} style={{ marginBottom: 6 }}>
          You've successfully joined <strong>{info?.account_name}</strong>
        </p>
        <p className={styles.statusMeta}>Role: <strong>{info?.role}</strong></p>
        <button onClick={() => navigate('/login')} className={styles.primaryBtn}>
          Log in to your account
        </button>
      </div>
    </Card>
  );

  // ── Sign up form (new user) ──────────────────────────────
  if (info?.needs_signup) return (
    <Card>
      <p className={styles.intro}>
        You've been invited to join a team on Manna Plag. Create your account to get started.
      </p>

      {/* Email — pre-filled, read only */}
      <div className={styles.field}>
        <label className={styles.label}>Email</label>
        <div className={styles.readonlyValue}>
          {info.email}
        </div>
      </div>

      {/* Full name */}
      <div className={styles.field}>
        <label className={styles.label}>Full name</label>
        <input
          className={`${styles.input} ${fieldErrors.fullName ? styles['input--error'] : ''}`}
          type="text"
          autoComplete="name"
          placeholder="Your full name"
          value={fullName}
          onChange={e => { setFullName(e.target.value); if (fieldErrors.fullName) setFieldErrors(prev => ({ ...prev, fullName: undefined })); }}
        />
        {fieldErrors.fullName && (
          <p className={styles.fieldError}>{fieldErrors.fullName}</p>
        )}
      </div>

      {/* Phone */}
      <div className={styles.field}>
        <label className={styles.label}>Phone</label>
        <input
          className={`${styles.input} ${fieldErrors.phone ? styles['input--error'] : ''}`}
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          placeholder="+91 9876543210"
          value={phone}
          onChange={e => { setPhone(e.target.value.replace(/[^0-9]/g, '')); if (fieldErrors.phone) setFieldErrors(prev => ({ ...prev, phone: undefined })); }}
        />
        {fieldErrors.phone && (
          <p className={styles.fieldError}>{fieldErrors.phone}</p>
        )}
      </div>

      {/* Password */}
      <div className={styles.field}>
        <label className={styles.label}>Password</label>
        <div className={styles.pwWrap}>
          <input
            className={`${styles.input} ${styles['input--withIcon']} ${fieldErrors.password ? styles['input--error'] : ''}`}
            type={showPw ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Minimum 8 characters"
            value={password}
            onChange={e => { setPassword(e.target.value); if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: undefined })); }}
          />
          <button
            type="button"
            onClick={() => setShowPw(!showPw)}
            aria-label={showPw ? 'Hide password' : 'Show password'}
            className={styles.pwToggle}>
            <EyeIcon open={showPw} />
          </button>
        </div>
        {fieldErrors.password && (
          <p className={styles.fieldError}>{fieldErrors.password}</p>
        )}
      </div>

      {/* Confirm password */}
      <div className={`${styles.field} ${styles['field--last']}`}>
        <label className={styles.label}>Confirm password</label>
        <input
          className={`${styles.input} ${fieldErrors.confirm ? styles['input--error'] : ''}`}
          type="password"
          autoComplete="new-password"
          placeholder="Re-enter your password"
          value={confirm}
          onChange={e => { setConfirm(e.target.value); if (fieldErrors.confirm) setFieldErrors(prev => ({ ...prev, confirm: undefined })); }}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
        />
        {fieldErrors.confirm && (
          <p className={styles.fieldError}>{fieldErrors.confirm}</p>
        )}
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error}
        </div>
      )}

      <button
        onClick={handleSubmit} disabled={submitting}
        className={styles.primaryBtn}>
        {submitting ? 'Creating account...' : 'Create account & join team'}
      </button>

      <p className={styles.footerText}>
        Already have an account?{' '}
        <Link to="/login" className={styles.footerLink}>Log in</Link>
      </p>
    </Card>
  );

  return null;
}